import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Proptypes from 'prop-types';
import Badge from '../../../../_shared/components/Badge';
import { formatLaunchTime } from '../../../../_shared/helpers/formatting';
import {
  TradeViewVariant,
  TradeState,
  GridCardVariant,
  GridCardPosition,
  TradeStateName
} from '../../../../_shared/types/trade';
import ActiveTimer from '../../../../_shared/components/ActiveTimer';
import { BidStatus } from '../../../../_shared/types/auction';
import BidBadge from '../../../../_shared/components/BidBadge';
import useLiveBids from '../../../../_shared/hooks/useLiveBids';
import {
  updateSelectedTradeIsWatchlisted,
  updateSelectedTradeIsAuctionEnded
} from '../../../../redux/slices/ui/selectedTrades';
import { tradeDetailsSlice } from '../../../../redux/slices/tradeDetails';
import Content from './Content';
import Footer from './Footer';
import useSelectedTrade from '../../../../_shared/hooks/trade/useSelectedTrade';
import MixpanelService from '../../../../services/MixpanelService';
import mixpanelConfig from '../../../../_shared/constants/mixpanelConfig';

import { StyledTradeCardContainer, StyledTradeCard } from '../Styles';
import {
  StyledInvalidTradeCard,
  StyledThumbnail,
  StyledTradeStatus,
  StyledActiveTimer,
  StyledBidBadgeContainer,
  StyledMakeModelYearContainer,
  StyledMakeModelYear,
  StyledMakeModel,
  StyledModel
} from './Styles';
import ReducerConstants from '../../../../_shared/constants/reducers';

const propTypes = {
  type: Proptypes.oneOf([
    GridCardVariant.EVENT_TRADE,
    GridCardVariant.NON_EVENT_TRADE
  ]).isRequired,
  tradeSummary: Proptypes.objectOf(Proptypes.any).isRequired,
  tradeState: Proptypes.oneOf([TradeState.upcoming, TradeState.active])
    .isRequired,
  bidModalRef: Proptypes.objectOf(Proptypes.any).isRequired,
  onSelect: Proptypes.func.isRequired,
  onImpound: Proptypes.func.isRequired,
  position: Proptypes.oneOf([
    GridCardPosition.LEFT,
    GridCardPosition.BETWEEN,
    GridCardPosition.RIGHT
  ]).isRequired,
  pendingActiveProxy: Proptypes.objectOf(Proptypes.any)
};

const defaultProps = {
  pendingActiveProxy: null
};

const TradeCard = ({
  type,
  tradeSummary,
  tradeState,
  bidModalRef,
  onSelect,
  onImpound,
  position,
  pendingActiveProxy
}) => {
  const mixpanel = MixpanelService.get();

  const [tradeCardSummary, setTradeCardSummary] = useState(tradeSummary);
  const [isStateChangedToActive, setIsStateChangedToActive] = useState(false);
  const [isAuctionEnded, setIsAuctionEnded] = useState(false);

  const { setBidContext, setBidModalOpen } = bidModalRef?.current ?? {};
  const dispatch = useDispatch();
  const { activeExpiryTime } = tradeSummary;

  const selectedTrade = useSelectedTrade(tradeState);
  const {
    id: tradeId,
    year,
    make,
    model,
    statusDates,
    auctionSummary
  } = tradeCardSummary;

  const { id: selectedTradeId } = selectedTrade;
  const isSelected = selectedTradeId === tradeId;

  const stateChange = useSelector(
    state => state?.[ReducerConstants.STATE_CHANGE][tradeId],
    shallowEqual
  );

  // Updates mini VDP BidBox when user edits proxy on trade card
  useEffect(() => {
    if (
      isSelected &&
      pendingActiveProxy &&
      pendingActiveProxy.tradeId === tradeId
    ) {
      const { bidAmount, proxyAmount } = pendingActiveProxy;
      if (tradeState === TradeState.upcoming) {
        dispatch(
          tradeDetailsSlice.actions.setUpcomingProxy({ bidAmount, proxyAmount })
        );
        dispatch(
          updateSelectedTradeIsWatchlisted({
            tradeState,
            isWatchlisted: true
          })
        );
      } else if (tradeState === TradeState.active) {
        dispatch(
          tradeDetailsSlice.actions.setActiveProxy({
            ...(bidAmount && { maxBid: bidAmount }),
            proxyAmount
          })
        );
        dispatch(
          updateSelectedTradeIsWatchlisted({
            tradeState,
            isWatchlisted: true
          })
        );
      }
    }
  }, [isSelected, pendingActiveProxy, dispatch, tradeState, tradeId]);

  // Updates trade card when user places a proxy on mini VDP BidBox
  useEffect(() => {
    if (isSelected) {
      if (tradeState === TradeState.upcoming) {
        if (selectedTrade.upcomingProxy) {
          setTradeCardSummary(prevTrade => ({
            ...prevTrade,
            upcomingProxy: selectedTrade.upcomingProxy
          }));
        }
      } else if (tradeState === TradeState.active) {
        if (selectedTrade.auctionSummary) {
          setTradeCardSummary(prevTrade => ({
            ...prevTrade,
            auctionSummary: selectedTrade.auctionSummary
          }));
        }
      }
    }
  }, [isSelected, selectedTrade, tradeState]);

  // Copied this from the list trade card
  useEffect(() => {
    if (tradeSummary) {
      setTradeCardSummary(tradeSummary);
    }
  }, [tradeSummary]);

  useLiveBids(
    tradeCardSummary,
    setTradeCardSummary,
    pendingActiveProxy,
    null,
    bidModalRef
  );

  const { id: launchId, values: launchValues } = formatLaunchTime(
    tradeState,
    statusDates,
    TradeViewVariant.LIST
  );

  const onAuctionEnded = useCallback(() => {
    setIsAuctionEnded(true);
  }, []);

  useEffect(() => {
    if (isSelected && isAuctionEnded) {
      dispatch(
        updateSelectedTradeIsAuctionEnded({
          tradeState,
          isAuctionEnded: true
        })
      );
    }
  }, [isAuctionEnded, dispatch, isSelected, tradeId, tradeState]);

  // detect if trade status changed to active and update state accordingly
  useEffect(() => {
    const auctionStarted =
      stateChange?.tradeId === tradeId &&
      stateChange?.previousState !== TradeState.active &&
      stateChange?.newState === TradeState.active;

    if (auctionStarted) {
      setIsStateChangedToActive(true);
    }
  }, [stateChange, tradeId]);

  const onSelectTradeCard = () => {
    mixpanel.track(mixpanelConfig.names.selectGridViewTrade, {
      trade_state: TradeStateName[tradeState],
      trade_id: tradeId,
      auction_id: auctionSummary?.id
    });
    onSelect(tradeId);
  };

  return (
    <>
      <StyledTradeCardContainer
        key={tradeCardSummary.id}
        variant={type}
        bidStatus={
          tradeCardSummary?.tradeState === TradeState.active &&
          auctionSummary?.userBidStatus
        }
        position={position}
        isSelected={isSelected}
        tradeState={tradeState}
      >
        <StyledTradeCard
          isSelected={isSelected}
          variant={
            tradeCardSummary?.tradeState === TradeState.active &&
            auctionSummary?.userBidStatus
          }
          onClick={onSelectTradeCard}
        >
          <StyledThumbnail url={tradeCardSummary?.thumbnailUrl} variant={type}>
            {tradeCardSummary?.tradeState === TradeState.upcoming &&
              launchId &&
              launchValues && (
                <StyledTradeStatus>
                  {!isStateChangedToActive ? (
                    <FormattedMessage id={launchId} values={launchValues} />
                  ) : (
                    <FormattedMessage
                      id="list.launchTime.active.today"
                      values={launchValues}
                    />
                  )}
                </StyledTradeStatus>
              )}
            {tradeCardSummary?.tradeState === TradeState.active && (
              <>
                <StyledActiveTimer>
                  <ActiveTimer
                    onAuctionEnded={onAuctionEnded}
                    activeExpiryTime={activeExpiryTime}
                    location={TradeViewVariant.LIST}
                    tradeId={tradeCardSummary?.id}
                    tradeCardAuctionEnded={isAuctionEnded}
                  />
                </StyledActiveTimer>
                <StyledBidBadgeContainer>
                  {(auctionSummary?.userBidStatus === BidStatus.topBidder ||
                    auctionSummary?.userBidStatus === BidStatus.proxy) && (
                    <BidBadge testId="bid-status-list" variant="success">
                      {/* High bid! */}
                      <FormattedMessage id="vdp.highbid" />
                    </BidBadge>
                  )}
                  {auctionSummary?.userBidStatus === BidStatus.outbid && (
                    <BidBadge testId="bid-status-list" variant="alert">
                      {/* Outbid! */}
                      <FormattedMessage id="vdp.outbid" />
                    </BidBadge>
                  )}
                </StyledBidBadgeContainer>
              </>
            )}
            <StyledMakeModelYearContainer>
              <StyledMakeModelYear>
                <Badge>{year}</Badge>
                <StyledMakeModel>
                  {make} <StyledModel>{model}</StyledModel>
                </StyledMakeModel>
              </StyledMakeModelYear>
            </StyledMakeModelYearContainer>
          </StyledThumbnail>
          <Content
            tradeSummary={tradeCardSummary}
            tradeState={tradeState}
            onImpound={onImpound}
            setBidContext={setBidContext}
            setBidModalOpen={setBidModalOpen}
          />
          <Footer
            tradeSummary={tradeCardSummary}
            tradeState={tradeState}
            isAuctionEnded={isAuctionEnded}
            onSelect={onSelect}
            setBidContext={setBidContext}
            setBidModalOpen={setBidModalOpen}
          />
        </StyledTradeCard>
        {((tradeState === TradeState.active && isAuctionEnded) ||
          (tradeState === TradeState.upcoming && isStateChangedToActive)) && (
          <StyledInvalidTradeCard />
        )}
      </StyledTradeCardContainer>
    </>
  );
};

TradeCard.propTypes = propTypes;
TradeCard.defaultProps = defaultProps;

export default TradeCard;
